import { DateTime } from 'luxon';
import { useTranslation } from 'react-i18next';
import { Box, Divider, Drawer, styled, Typography } from '@mui/material';
import { FormProvider, useForm } from 'react-hook-form';
import { CollapsibleSection, LwButton, MultiDateCalendar } from 'redesign';
import { useCalendarDefaults } from 'shared/hooks/useCalendarDefaults';
import { dateTimeToDateObject } from 'shared/components/shift-forms/components/shift-multiply-calendar/multi-date-object-utils';
import { TimeSlotWrapper } from './time-slot-wrapper';
import { AvailabilityWorkersMultiselect } from './availability-workers-multiselect';
import { SegmentsForEdit, TimeSlotFormContext } from './time-slot.types';
import { useHandleAvailabilitySubmission } from '../hooks/use-handle-availability-submission';

export type AvailabilityForEdit = {
  selectedWorker: { id: string; name: string };
  availabilitySegments: SegmentsForEdit[];
  availabilityDate: DateTime;
};

export const EditAvailabilitySidebar = ({
  availabilityForEdit,
  onClose,
  isOpen,
  fromDate,
  searchTerm,
}: {
  availabilityForEdit: AvailabilityForEdit;
  onClose: () => void;
  isOpen: boolean;
  fromDate: DateTime;
  searchTerm: string | undefined;
}) => {
  const { availabilityDate, selectedWorker, availabilitySegments } = availabilityForEdit;
  const { t } = useTranslation('all-platforms-common');
  const { monthLabels, dayLabels } = useCalendarDefaults();
  const formMethods = useForm<TimeSlotFormContext>({
    defaultValues: {
      workerOptions: [selectedWorker],
      availability: availabilitySegments,
    },
  });
  const { handleDeleteAll, handleSubmit } = useHandleAvailabilitySubmission({
    workerId: selectedWorker.id,
    formMethods,
    originalAvailability: availabilitySegments,
    availabilityDate,
    fromDate,
    searchTerm,
    cancel: onClose,
  });

  return (
    <Drawer
      title="Edit Availability"
      anchor="right"
      open={isOpen}
      onClose={onClose}
      PaperProps={{ style: { width: 600 } }}
    >
      <FormWrapper>
        <Box>
          <Typography variant="h1" mb={4}>
            {t('edit_availability.title')}
          </Typography>
        </Box>
        <Divider />
        <FormProvider {...formMethods}>
          <form onSubmit={handleSubmit}>
            <FormBody>
              <CollapsibleSection title="Workers">
                <AvailabilityWorkersMultiselect workerOptions={[]} disabled={true} />
              </CollapsibleSection>
              <CollapsibleSection title="Availability">
                <CalendarWrapper data-testid="edit-availability-calendar">
                  <MultiDateCalendar
                    value={dateTimeToDateObject(availabilityDate)}
                    currentDate={dateTimeToDateObject(availabilityDate.startOf('month'))}
                    mapDays={({ date, isSameDate }) => {
                      return {
                        children: date.format('DD'),
                        disabled: true,
                        style: {
                          backgroundColor: isSameDate(dateTimeToDateObject(availabilityDate), date)
                            ? '#002BFF'
                            : 'inherit',
                        },
                      };
                    }}
                    weekStartDayIndex={1}
                    buttons={false}
                    multiple
                    disableMonthPicker
                    disableYearPicker
                    months={monthLabels}
                    weekDays={dayLabels}
                    numberOfMonths={2}
                  />
                </CalendarWrapper>
                <SectionDivider />
                <TimeSlotWrapper />
              </CollapsibleSection>
              <FormActionContainer>
                <LwButton onClick={handleDeleteAll} color="secondary">
                  {t('edit_availability.delete_all')}
                </LwButton>
                <StandardActionContainer>
                  <LwButton onClick={onClose} color="secondary">
                    {t('edit_availability.close')}
                  </LwButton>
                  <LwButton color="primary" type="submit">
                    {t('edit_availability.save')}
                  </LwButton>
                </StandardActionContainer>
              </FormActionContainer>
            </FormBody>
          </form>
        </FormProvider>
      </FormWrapper>
    </Drawer>
  );
};

const FormWrapper = styled(Box)(({ theme }) => ({
  height: '100%',
  padding: theme.spacing(8),
  overflow: 'auto',
}));
const StandardActionContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
}));
const FormBody = styled(Box)(({ theme }) => ({
  paddingTop: theme.spacing(4),
  width: '550px',
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(4),
}));

const FormActionContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignContent: 'center',
  marginTop: theme.spacing(4),
}));

const SectionDivider = styled(Divider)(({ theme }) => ({
  marginTop: theme.spacing(4),
  marginBottom: theme.spacing(4),
}));

const CalendarWrapper = styled(Box)(({ theme }) => ({
  '&& .rmdp-week-day': {
    width: theme.spacing(8),
    height: theme.spacing(4),
  },

  '&& .rmdp-day': {
    width: theme.spacing(8),
    height: theme.spacing(8),
    margin: '2px',
  },

  '&& .rmdp-header': {
    height: 'auto',
    width: '100%',
    justifyContent: 'space-between',
    '& .rmdp-header-values': {
      padding: theme.spacing(2),
      '& > span': {
        paddingLeft: theme.spacing(2),
      },
    },
  },
}));
